import { get, find, isEmpty } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import { pluralize } from '../../../../../../utils/userflow';

import Button from '../../../../../Button';
import CallIcon from '../../../../../CallIcon';
import CallReason from '../../../../Calls/CallReason';
import CallReasons from '../../../../Calls/CallReasons';
import Duration from '../../../../../Duration';
import Name from '../../../../Clients/Name';
import Note from '../../../../Notes/Note';
import TicketType from '../../../../Tickets/TicketType';
import { TICKET_TYPES } from '../../../../../../constants';
import TimeFrom from '../../../../Common/TimeFrom';
import Timer from '../../../../../Timer';
import Messenger from '../../../../Chains/Messenger';
import CallSummary from './CallSummary';

import {
  Bot as BotIcon,
  Mail as EmailIcon,
  Note as NoteIcon,
  Message as MessageIcon,
  Pinned as PinnedIcon,
  Read as ReadIcon,
  Record as RecordIcon,
} from '@icons/communication';
import { Pin as PinIcon, Close as DeleteIcon } from '@icons/common';

import IClientLog from '../../interfaces/IClientLog';

import { getCallDuration } from '../../../../../../utils/date';

import {
  LOG_RELATIONS,
  CLIENT_LOG_TYPES as Types,
  MESSENGER_TYPES as MessengerTypes,
} from '../../../../../../constants';
import { ValueOf } from '@utils/tsUtils';

const UPDATE_INTERVAL = 1 * 1000;
const CLASS_NAME_POPED_FORM = 'poped_form';
const MESSAGE_COUNT_PLURALIZE = ['сообщение', 'сообщения', 'сообщений'];

interface Props extends IClientLog {
  className?: string;
  isCallSummaryExpanded: boolean;
  selectedCallId?: string | null;
  onClickOutside(params: {
    preventScroll: boolean;
    communicationBottom: number;
  }): void;
  onClickIcon: (params: {
    id: string;
    type: (typeof Types)[keyof typeof Types];
    isBot: boolean;
    ticketId?: string;
  }) => void;
  onDelete(params: { id: string }): void;
  onEdit(params: {
    id: string;
    value: string;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  }): void;
  onExpiredEditable(): void;
  onPin: (params: {
    id: string;
    isPinned: boolean;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  }) => void;
  onReasonDelete(params: { callId: string; [propName: string]: unknown }): void;
  onReasonSubmit(params: { callId: string; [propName: string]: unknown }): void;
  onCallSummaryToggle(callId: string): void;
  onDeleteAttachment(params: {
    attachId: string;
    noteId: string;
    relateTo: ValueOf<typeof LOG_RELATIONS>;
  }): void;
}

interface State {
  isEditing: boolean;
  isEditableExpired: boolean;
  selectedReasonGroupId: string | null;
}

const defaultProps: Partial<Props> = {
  active: false,
  className: '',
  ticketType: TICKET_TYPES.UNKNOWN,
  read: false,
  selectedCallId: null,
};

class ClientLog extends React.PureComponent<Props, State> {
  private communicationRef: React.RefObject<HTMLInputElement>;

  private reasonsRef: React.RefObject<HTMLInputElement>;

  private wrapperRef: React.RefObject<HTMLInputElement>;

  interval: number | NodeJS.Timeout;

  static defaultProps = defaultProps as Partial<Props>;

  constructor(props: Props) {
    super(props);
    const { type } = props;

    this.state = {
      isEditing: false,
      isEditableExpired: this.isExpired(),
      selectedReasonGroupId: null,
    };

    if (type === Types.CALL) {
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
      this.handleReasonDelete = this.handleReasonDelete.bind(this);
      this.handleReasonsSubmit = this.handleReasonsSubmit.bind(this);
    }

    if (type === Types.NOTE) {
      this.togglePin = this.togglePin.bind(this);
      this.handleDelete = this.handleDelete.bind(this);
      this.handleNoteEdit = this.handleNoteEdit.bind(this);
      this.handleDeleteAttachment = this.handleDeleteAttachment.bind(this);
    }

    this.interval = 0;
    this.communicationRef = React.createRef();
    this.reasonsRef = React.createRef();
    this.wrapperRef = React.createRef();
    this.toggleEdit = this.toggleEdit.bind(this);
    this.handleIconClick = this.handleIconClick.bind(this);
  }

  componentDidMount() {
    const { note, call } = this.props;
    const isEditable = !!get(call || note, 'editable');
    const hasExpirationTime = !!get(call || note, 'editableExpiresAt');
    if (isEditable && hasExpirationTime && !this.isExpired()) {
      this.interval = setInterval(
        this.checkExpired.bind(this),
        UPDATE_INTERVAL
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { isEditableExpired } = this.state;
    const { onExpiredEditable } = this.props;
    if (isEditableExpired !== prevState.isEditableExpired) {
      // TODO: Replace setTimeout
      setTimeout(onExpiredEditable);
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval as NodeJS.Timeout);
  }

  handleDelete() {
    const { note, onDelete } = this.props;
    const id = get(note, 'id');

    if (id && onDelete) {
      onDelete({ id });
    }
  }

  handleOutsideClick() {
    const { onClickOutside } = this.props;
    const { isEditing: preventScroll } = this.state;

    let communicationBottom = 0;
    if (preventScroll) {
      const reasonsForm = this.reasonsRef.current;
      if (reasonsForm) {
        communicationBottom += reasonsForm.offsetHeight;
      }

      if (this.communicationRef) {
        const el = this.communicationRef.current;
        if (el) {
          communicationBottom += el.getBoundingClientRect().top;
          communicationBottom += el.offsetHeight;
        }
      }
    }

    onClickOutside({ communicationBottom, preventScroll });
  }

  handleNoteEdit({ value }) {
    const { note, onEdit } = this.props;
    const id = get(note, 'id');

    if (id && onEdit) {
      onEdit({
        id,
        value,
        relateTo: LOG_RELATIONS.CLIENT,
      });
    }
  }

  handleIconClick() {
    const { call, email, type, ticketId, onClickIcon } = this.props;

    const node = email || call;
    const id = String(get(node, 'id'));
    const isBot = get(node, 'isBot') as any;

    if (id && type) {
      onClickIcon({
        id,
        type,
        isBot,
        ticketId,
      });
    }
  }

  handleReasonGroupEdit({ selectedReasonGroupId }) {
    this.setState({ selectedReasonGroupId }, this.toggleEdit);
  }

  handleReasonDelete(data) {
    const { call, onReasonDelete } = this.props;
    const callId = get(call, 'id');
    onReasonDelete({ ...data, callId });
  }

  handleReasonsSubmit(data) {
    const { call, onReasonSubmit } = this.props;
    const callId = get(call, 'id');
    onReasonSubmit({ ...data, callId });
    this.toggleEdit();
  }

  togglePin() {
    const { note, onPin } = this.props;
    const id = get(note, 'id');
    const isPinned = get(note, 'isPinned');

    if (id && onPin) {
      onPin({
        id,
        isPinned: !isPinned,
        relateTo: LOG_RELATIONS.CLIENT,
      });
    }
  }

  toggleEdit() {
    this.setState(
      prevState => ({
        isEditing: !prevState.isEditing,
      }),
      this.handleOutsideClick
    );
  }

  isExpired() {
    const { note, call } = this.props;
    const editExpitationTime = get(call || note, 'editableExpiresAt') || null;

    return Boolean(
      editExpitationTime && Date.now() >= editExpitationTime * 1000
    );
  }

  checkExpired() {
    if (this.isExpired()) {
      clearInterval(this.interval as NodeJS.Timeout);
      this.setState({
        isEditableExpired: true,
      });
    }
  }

  reasonListRenderer(isEditable) {
    const { call } = this.props;
    const { isEditing } = this.state;

    const reasonGroups = get(call, 'reasonGroups');
    const reasons = get(call, 'reasons');
    const selectedReasonIds = get(call, 'selectedReasonIds') || [];

    const selectedGroups = {};
    selectedReasonIds.forEach(value => {
      const reason = find(reasons, { value });
      if (reason) {
        const { groupId } = reason;
        const existingReasons = selectedGroups[groupId] || [];
        existingReasons.push(reason);
        selectedGroups[groupId] = existingReasons;
      }
    });

    return Object.keys(selectedGroups).map(id => {
      const reason = find(reasonGroups, { value: id });

      if (reason) {
        const { title, value } = reason;
        const groupReasons = selectedGroups[id];

        return (
          <StyledReason
            key={title}
            type={title}
            reasons={groupReasons}
            readOnly={!isEditable || isEditing}
            onDelete={this.handleReasonDelete}
            onEditToggle={() =>
              this.handleReasonGroupEdit({ selectedReasonGroupId: value })
            }
          />
        );
      }
    });
  }

  handleDeleteAttachment(params: { attachId: string; noteId: string }) {
    const { onDeleteAttachment } = this.props;

    onDeleteAttachment({ ...params, relateTo: LOG_RELATIONS.CLIENT });
  }

  render() {
    const {
      call,
      email,
      name,
      note,
      group,
      text,
      type,
      read,
      ticketId,
      selectedCallId,
      isCallSummaryExpanded,
      onCallSummaryToggle,
      ticketType = TICKET_TYPES.UNKNOWN,
      time,
      onDeleteAttachment,
      ...props
    } = this.props;

    const { isEditing, isEditableExpired, selectedReasonGroupId } = this.state;

    const callId = get(call, 'id');
    const answeredAt = get(call, 'answeredAt');
    const callUrl = get(call, 'callUrl');
    const finalCallSummary = get(call, 'finalCallSummary');
    const finishedAt = get(call, 'finishedAt');
    const isAnswered = get(call, 'isAnswered');
    const isCallback = get(call, 'isCallback');
    const isIncoming = get(call, 'isIncoming');
    const reasonDescription = get(call, 'reasonDescription');
    const reasonGroups = get(call, 'reasonGroups') || [];
    const reasons = get(call, 'reasons');
    const recordUrl = get(call, 'recordUrl');
    const requireNoteIds = get(call, 'requireNoteIds');
    const segments = get(call, 'segments');
    const selectedReasonIds = get(call, 'selectedReasonIds') || [];
    const selectedSegmentId = get(call, 'selectedSegmentId');
    const selectedSegment = find(segments, { value: selectedSegmentId });

    const noteId = get(note, 'id');
    const isPinned = get(note, 'isPinned');
    const noteText = get(note, 'text');
    const attachments = get(note, 'attachments');

    const groupCount = get(group, 'count');
    const groupMessenger = get(group, 'messenger');

    const isBotEmail = get(email, 'isBot');

    const editable = get(call || note, 'editable');

    const isActive = !finishedAt && type === Types.CALL;
    const isSelected = selectedCallId === callId && type === Types.CALL;
    const hasCallSummary =
      type === Types.CALL &&
      finalCallSummary &&
      callId &&
      (selectedCallId == null || selectedCallId === callId);
    const isEditable = editable && !isEditableExpired;
    const isEditableCall =
      type === Types.CALL && isEditable && !isEmpty(reasons);
    const isEditableButHasNoReasons =
      isEditableCall && !isEditing && isEmpty(selectedReasonIds);
    const isIconClickable = type === Types.EMAIL && (ticketId || isBotEmail);

    const duration = getCallDuration(answeredAt, finishedAt);

    let content;
    switch (type) {
      case Types.CALL:
        content = (
          <>
            <StyledHead>
              {isActive && duration >= 0 && (
                <StyledTimer initialSeconds={duration} />
              )}
              {!isActive && <StyledDuration duration={duration} />}
              {name && <StyledName name={name} />}
            </StyledHead>
            {time && <TimeFrom full extend time={time} />}
            {recordUrl && (
              <a href={recordUrl} target="_blank" title="Прослушать запись">
                <StyledRecordIcon />
              </a>
            )}
            {selectedSegment && (
              <StyledSegment>{selectedSegment.title}</StyledSegment>
            )}
            {!isEmpty(reasons) && this.reasonListRenderer(isEditableCall)}
            {reasonDescription && (
              <StyledReasonsDescription>
                {reasonDescription}
              </StyledReasonsDescription>
            )}
            {hasCallSummary && (
              <CallSummary
                callId={callId}
                finalCallSummary={finalCallSummary}
                isExpanded={isCallSummaryExpanded}
                onToggle={() => onCallSummaryToggle(callId)}
              />
            )}
          </>
        );
        break;

      case Types.EMAIL:
        content = (
          <>
            {text && <StyledText title={text}>{text}</StyledText>}
            {time && <TimeFrom full extend time={time} />}
            {name && <StyledName name={name} />}
            {read && <StyledReadStatus />}
            {ticketId && <StyledTicketType id={ticketId} type={ticketType} />}
          </>
        );
        break;

      case Types.NOTE:
        content = (
          <StyledNoteContainer>
            <StyledInfo>
              <StyledMeta>
                {name && <StyledName name={name} />}
                {time && <TimeFrom full extend time={time} />}
              </StyledMeta>
              {!isPinned && (
                <StyledIconButton
                  functional
                  title="Закрепить"
                  onClick={this.togglePin}
                >
                  <PinIcon height={15} />
                </StyledIconButton>
              )}
              {isEditable && !isEditing && (
                <StyledDeleteButton
                  functional
                  title="Удалить"
                  onClick={this.handleDelete}
                >
                  <StyledDeleteIcon />
                </StyledDeleteButton>
              )}
            </StyledInfo>
            <StyledNote
              id={noteId}
              name={name}
              onEdit={this.handleNoteEdit}
              onToggleEdit={this.toggleEdit}
              readOnly={!isEditable}
              time={time}
              value={noteText}
              attachments={attachments}
              onDeleteAttachment={this.handleDeleteAttachment}
            />
          </StyledNoteContainer>
        );
        break;

      case Types.MESSAGE:
      case Types.SMS:
        content = (
          <>
            {text && <StyledText title={text}>{text}</StyledText>}
            {time && <TimeFrom full extend time={time} />}
            {name && <StyledName name={name} />}
          </>
        );
        break;

      case Types.MESSAGE_GROUP:
        content = (
          <>
            <StyledText>
              {groupCount} {pluralize(groupCount, MESSAGE_COUNT_PLURALIZE)}
            </StyledText>
            <StyledMetaText>
              {name ? <StyledHighlight>{name}</StyledHighlight> : 'Клиент '}
              оставил/а сообщение
            </StyledMetaText>
            {time && <TimeFrom full extend time={time} />}
            {ticketId && <StyledTicketType id={ticketId} type={ticketType} />}
          </>
        );
        break;

      default:
        // TODO: Need to add empty card for communication without type.
        content = null;
        break;
    }

    return (
      <StyledCommunicationWrapper ref={this.wrapperRef} {...props}>
        <StyledCommunication
          ref={this.communicationRef}
          isSelected={isSelected}
        >
          <StyledTimeline offset={isEditableButHasNoReasons ? 62 : 0}>
            <StyledIconButton
              functional
              disabled={!isIconClickable}
              onClick={this.handleIconClick}
            >
              <StyledTypeIcon
                href={callUrl}
                isActive={isActive}
                isAnswered={Boolean(isAnswered)}
                isCallback={Boolean(isCallback)}
                isIncoming={Boolean(isIncoming)}
                isBot={Boolean(isBotEmail)}
                onPin={this.togglePin}
                pinned={Boolean(isPinned)}
                type={type}
                messengerType={groupMessenger}
              />
            </StyledIconButton>
          </StyledTimeline>
          <StyledContent>{content}</StyledContent>
          {isEditableButHasNoReasons && (
            <StyledReasonAdd onClick={this.toggleEdit}>
              Указать причину обращения
            </StyledReasonAdd>
          )}
        </StyledCommunication>
        {isEditableCall && isEditing && (
          <StyledReasons
            className={CLASS_NAME_POPED_FORM}
            description={reasonDescription}
            groups={reasonGroups}
            onClickOutside={this.toggleEdit}
            onSubmit={this.handleReasonsSubmit}
            reasons={reasons}
            ref={this.reasonsRef}
            requireNoteIds={requireNoteIds}
            segments={segments}
            selectedGroupId={selectedReasonGroupId}
            selectedReasonIds={selectedReasonIds}
            selectedSegmentId={selectedSegmentId}
          />
        )}
      </StyledCommunicationWrapper>
    );
  }
}

const StyledCommunicationWrapper = styled.div`
  display: block;
`;

const StyledInfo = styled.div`
  display: flex;
  font-size: 12px;
  color: #919499;
  margin-bottom: 4px;
`;

const StyledMeta = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StyledCallIcon = styled(CallIcon)<{
  isActive: boolean;
  isAnswered: boolean;
  isCallback: boolean;
  isIncoming: boolean;
}>`
  svg > * {
    fill: none;
    stroke: #919499;
  }

  ${({ isActive, isCallback }: { isActive: boolean; isCallback: boolean }) =>
    isActive &&
    !isCallback &&
    `
    svg > * {
      fill: #00c253;
      stroke: #00c253;
    }
  `}
`;

const StyledIconButton = styled(Button)`
  display: flex;
  outline: none;

  ${({ disabled }) =>
    !disabled &&
    `
    &:hover path { stroke: #007aff; }
  `}
`;

// @ts-ignore
const StyledDeleteButton = styled(StyledIconButton)`
  margin-left: 10px;
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  width: 12px;
  height: 12px;
`;

const StyledTypeIcon = styled(
  ({
    isActive,
    isAnswered,
    isBot,
    isCallback,
    isIncoming,
    onPin,
    pinned: isPinned,
    type,
    messengerType,
    ...props
  }: {
    isActive: boolean;
    isAnswered: boolean;
    isBot: boolean;
    isCallback: boolean;
    isIncoming: boolean;
    onPin(params: { id: string; isPinned: boolean }): void;
    pinned: boolean;
    type: (typeof Types)[keyof typeof Types];
    messengerType: (typeof MessengerTypes)[keyof typeof MessengerTypes];
    [propName: string]: unknown;
  }) => {
    let icon;
    switch (type) {
      case Types.CALL:
        icon = (
          <StyledCallIcon
            isActive={isActive}
            isAnswered={isAnswered}
            isCallback={isCallback}
            isIncoming={isIncoming}
          />
        );
        break;

      case Types.EMAIL:
        icon = isBot ? <BotIcon /> : <EmailIcon />;
        break;

      case Types.NOTE: {
        icon = isPinned ? (
          <StyledPinButton onClick={onPin} title="Открепить">
            <StyledPin />
          </StyledPinButton>
        ) : (
          <StyledNoteIcon />
        );
        break;
      }

      case Types.MESSAGE:
      case Types.SMS:
        icon = <MessageIcon />;
        break;

      case Types.MESSAGE_GROUP:
        icon = <Messenger type={messengerType} />;
        break;

      default:
        return null;
    }

    return React.cloneElement(icon, props);
  }
)`
  background-color: #e6e6e6;
  box-sizing: content-box;
  height: 17px;
  margin-top: 12px;
  padding: 4px 0;
  z-index: 2;
  width: 17px;
`;

const StyledCommunication = styled.div<{ isSelected: boolean }>`
  background-color: #e6e6e6;
  display: flex;
  padding: 10px;
  box-sizing: border-box;
  position: relative;
  width: 100%;
  font-size: 14px;
  border-radius: 6px;
  flex-wrap: wrap;

  ${({ isSelected }: { isSelected: boolean }) =>
    isSelected &&
    `
    border-top: 1px solid #e6e6e6;
    background-color: #fff;
    
    ${StyledTypeIcon} {
      background-color: #fff;
    }
  `}
`;

const StyledReasons = styled(CallReasons)<{
  [index: string]: unknown;
}>`
  background-color: #fff;
  display: flex;
  box-sizing: border-box;
  padding: 10px 0;
  position: relative;
  width: 100%;
  font-size: 14px;
  border-radius: 6px;
  flex-wrap: wrap;
  z-index: 5;
`;

const StyledReason = styled(CallReason)<{
  [index: string]: unknown;
}>`
  margin-top: 5px;
`;

const StyledSegment = styled.div`
  margin-top: 5px;
`;

const StyledReasonsDescription = styled.div`
  margin-top: 5px;
`;

const StyledHead = styled.div`
  display: flex;
  margin-top: 4px;
`;

export const StyledTimeline = styled.div<{ offset: number }>`
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 32px;

  :before {
    background-color: #c2c5cc;
    content: '';
    height: 100%;
    left: 50%;
    margin-left: -1px;
    position: absolute;
    top: 0;
    width: 1px;
  }

  ${({ offset }: { offset: number }) =>
    offset &&
    `
    height: calc(100% - ${offset}px);
  `}
`;

const StyledPinButton = styled(Button)`
  z-index: 2;
  width: 17px;
  height: 17px;
  border: none;
  padding: 4px 0;
  margin-top: 12px;
  line-height: normal;
  box-sizing: content-box;
  background-color: #e6e6e6;
`;

const StyledPin = styled(PinnedIcon)`
  path {
    fill: none;
  }
  &:hover path {
    stroke: #007aff;
  }
`;

const StyledNoteIcon = styled(NoteIcon)`
  margin-top: 13px;
`;

export const StyledContent = styled.div`
  color: #4d4d4d;
  display: flex;
  flex-direction: column;
  margin-left: 22px;
  min-height: 30px;
  position: relative;
  width: 100%;
`;

const StyledText = styled.div`
  display: inline-block;
  margin-right: 5px;
  height: auto;
`;

const StyledHighlight = styled.span`
  color: #007aff;
  margin-right: 3px;
  font-weight: normal;
`;

const StyledMetaText = styled.div`
  display: inline-block;
  margin-right: 5px;
  height: auto;
  font-size: 13px;
  color: #919499;
`;

const StyledDuration = styled(Duration)<{ duration: number }>`
  font-size: 14px;
`;

const StyledTimer = styled(Timer)`
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  font-weight: normal;
  letter-spacing: -0.5px;
`;

const StyledNote = styled(Note)``;

const StyledTicketType = styled(TicketType)<{
  id: string;
  type: (typeof TICKET_TYPES)[keyof typeof TICKET_TYPES];
}>`
  margin-top: 5px;
`;

const StyledName = styled(Name)`
  color: #479df4;
  font-size: 13px;
  font-weight: 400;
  flex: 1;

  ${StyledDuration} + &,
  ${StyledTimer} + & {
    margin-left: 5px;
  }
`;

const StyledReadStatus = styled(ReadIcon)`
  height: 12px;
  position: absolute;
  right: 0;
  top: 2px;
  width: 20px;
`;

const StyledRecordIcon = styled(RecordIcon)`
  cursor: pointer;
  height: 23px;
  position: absolute;
  right: 0;
  top: 0;
  width: 22px;
`;

const StyledReasonAdd = styled(Button)`
  background-color: #007aff;
  border-radius: 4px;
  color: #ffffff;
  font-size: 14px;
  margin-top: 10px;
  width: 100%;

  &:hover {
    color: #ffffff;
  }
`;

const StyledNoteContainer = styled.div`
  background-color: #fffbe5;
  border-radius: 4px;

  ${StyledInfo} {
    padding: 10px 10px 0;
  }

  ${StyledNote} {
    padding: 0 10px 10px;
    background: #fffbe5;
    border-radius: 4px;
    box-shadow: 0px 2px 2px rgb(230 230 230);
  }
`;

export default ClientLog;
